import api from './apiConfig';
import { SPEAKING_SUBMIT_FAILED_USER_MESSAGE } from '../constants/speakingPracticeMessages';
import { getAudioFileMeta } from '../components/Learner/SpeakingPractice/utils/audioUtils';

// Get Quick Practice Questions
// POST /frontsite-api/v1/learner/learning/practice/get
// Body: { "data": { "practice-type": "quick-practice", "course-id"?: "uuid", "subcourse-id"?: "uuid", "skill-id"?: "uuid", "lesson-id"?: "uuid" } }
export const getQuickPracticeQuestions = async ({ courseId, subcourseId, skillId, lessonId }) => {
  try {
    console.log('🌐 API CALL: getQuickPracticeQuestions - Starting request...');
    
    // Build payload - must have at least one ID and practice-type
    const payload = {
      data: {
        'practice-type': 'quick-practice',
      },
    };

    // changed by Cursor: validate at least one ID is provided before calling API
    if (!lessonId && !skillId && !subcourseId && !courseId) {
      const errorMsg = 'At least one ID is required: lesson-id, skill-id, subcourse-id, or course-id';
      console.error('🌐 API CALL: getQuickPracticeQuestions - Validation error:', errorMsg);
      return { success: false, message: errorMsg };
    }

    // Priority: lesson-id > skill-id > subcourse-id > course-id
    if (lessonId) {
      payload.data['lesson-id'] = lessonId;
    } else if (skillId) {
      payload.data['skill-id'] = skillId;
    } else if (subcourseId) {
      payload.data['subcourse-id'] = subcourseId;
    } else if (courseId) {
      payload.data['course-id'] = courseId;
    }

    console.log('🌐 API CALL: URL:', api.defaults.baseURL + '/frontsite-api/v1/learner/learning/practice/get');
    console.log('🌐 API CALL: Body:', payload);

    const response = await api.post('/frontsite-api/v1/learner/learning/practice/get', payload);
    
    console.log('🌐 API CALL: getQuickPracticeQuestions - Response received:', response);

    if (response.data?.status === 'error') {
      return {
        success: false,
        message: response.data?.message || 'Failed to get quick practice questions',
      };
    }
    
    return {
      success: response.data?.status === 'ok',
      data: response.data?.data,
      message: response.data?.message,
    };
  } catch (error) {
    console.error('🌐 API CALL: getQuickPracticeQuestions - Error:', error);
    console.error('🌐 API CALL: Error response:', error.response);
    return {
      success: false,
      message: error.response?.data?.message || error.message,
    };
  }
};

// Get Speaking Practice Questions
// POST /frontsite-api/v1/learner/learning/practice/get
// Body: { "data": { "practice-type": "speaking-practice", "course-id"?: "uuid", "subcourse-id"?: "uuid", "skill-id"?: "uuid", "lesson-id"?: "uuid" } }
export const getSpeakingPracticeQuestions = async ({ courseId, subcourseId, skillId, lessonId }) => {
  try {
    console.log('🌐 API CALL: getSpeakingPracticeQuestions - Starting request...');
    
    // Build payload - must have at least one ID and practice-type
    const payload = {
      data: {
        'practice-type': 'speaking-practice',
      },
    };

    // Priority: lesson-id > skill-id > subcourse-id > course-id
    if (lessonId) {
      payload.data['lesson-id'] = lessonId;
    } else if (skillId) {
      payload.data['skill-id'] = skillId;
    } else if (subcourseId) {
      payload.data['subcourse-id'] = subcourseId;
    } else if (courseId) {
      payload.data['course-id'] = courseId;
    }

    // Validate: at least one ID must be provided
    if (!lessonId && !skillId && !subcourseId && !courseId) {
      const errorMsg = 'At least one ID is required: lesson-id, skill-id, subcourse-id, or course-id';
      console.error('🌐 API CALL: getSpeakingPracticeQuestions - Validation error:', errorMsg);
      return {
        success: false,
        message: errorMsg,
      };
    }

    console.log('🌐 API CALL: URL:', api.defaults.baseURL + '/frontsite-api/v1/learner/learning/practice/get');
    console.log('🌐 API CALL: Body:', payload);

    const response = await api.post('/frontsite-api/v1/learner/learning/practice/get', payload);
    
    console.log('🌐 API CALL: getSpeakingPracticeQuestions - Response received:', response);
    
    // Check if response status is error
    if (response.data?.status === 'error') {
      const errorMessage = response.data?.message || 'Failed to get speaking practice questions';
      console.error('🌐 API CALL: getSpeakingPracticeQuestions - Error status in response:', errorMessage);
      return {
        success: false,
        message: errorMessage,
      };
    }
    
    return {
      success: response.data?.status === 'ok',
      data: response.data?.data,
      message: response.data?.message,
    };
  } catch (error) {
    console.error('🌐 API CALL: getSpeakingPracticeQuestions - Error:', error);
    console.error('🌐 API CALL: Error response:', error.response);
    console.error('🌐 API CALL: Error response data:', error.response?.data);
    
    // Provide more detailed error message
    let errorMessage = error.message || 'Failed to get speaking practice questions';
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.response?.status === 400) {
      errorMessage = 'Bad Request: Invalid parameters. Please check that at least one ID (lesson-id, skill-id, subcourse-id, or course-id) is provided.';
    }
    
    return {
      success: false,
      message: errorMessage,
      httpStatus: error.response?.status,
    };
  }
};

// Get Writing Practice Questions
// POST /frontsite-api/v1/learner/learning/practice/get
// Body: { "data": { "practice-type": "writing-practice", "course-id"?: "uuid", "subcourse-id"?: "uuid", "skill-id"?: "uuid", "lesson-id"?: "uuid" } }
export const getWritingPracticeQuestions = async ({ courseId, subcourseId, skillId, lessonId }) => {
  try {
    console.log('🌐 API CALL: getWritingPracticeQuestions - Starting request...');
    
    // Build payload - must have at least one ID and practice-type
    const payload = {
      data: {
        'practice-type': 'writing-practice',
      },
    };

    // Priority: lesson-id > skill-id > subcourse-id > course-id
    if (lessonId) {
      payload.data['lesson-id'] = lessonId;
    } else if (skillId) {
      payload.data['skill-id'] = skillId;
    } else if (subcourseId) {
      payload.data['subcourse-id'] = subcourseId;
    } else if (courseId) {
      payload.data['course-id'] = courseId;
    }

    console.log('🌐 API CALL: URL:', api.defaults.baseURL + '/frontsite-api/v1/learner/learning/practice/get');
    console.log('🌐 API CALL: Body:', payload);

    const response = await api.post('/frontsite-api/v1/learner/learning/practice/get', payload);
    
    console.log('🌐 API CALL: getWritingPracticeQuestions - Response received:', response);
    
    return {
      success: response.data?.status === 'ok',
      data: response.data?.data,
      message: response.data?.message,
    };
  } catch (error) {
    console.error('🌐 API CALL: getWritingPracticeQuestions - Error:', error);
    console.error('🌐 API CALL: Error response:', error.response);
    return {
      success: false,
      message: error.response?.data?.message || error.message,
    };
  }
};

// Get Drill Vocab Questions
// POST /frontsite-api/v1/learner/learning/vocab-drill/get
// Body: { "data": { "subcourse-id": "uuid" } }
export const getDrillVocabQuestions = async ({ subcourseId }) => {
  try {
    console.log('🌐 API CALL: getDrillVocabQuestions - Starting request...');

    if (!subcourseId) {
      const errorMsg = 'subcourse-id is required for vocab drill';
      console.error('🌐 API CALL: getDrillVocabQuestions - Validation error:', errorMsg);
      return { success: false, message: errorMsg };
    }

    const payload = {
      data: {
        'subcourse-id': subcourseId,
      },
    };

    console.log('🌐 API CALL: URL:', api.defaults.baseURL + '/frontsite-api/v1/learner/learning/vocab-drill/get');
    console.log('🌐 API CALL: Body:', payload);

    const response = await api.post('/frontsite-api/v1/learner/learning/vocab-drill/get', payload);

    console.log('🌐 API CALL: getDrillVocabQuestions - Response received:', response);

    if (response.data?.status === 'error') {
      return {
        success: false,
        message: response.data?.message || 'Failed to get vocab drill questions',
      };
    }

    return {
      success: response.data?.status === 'ok',
      data: response.data?.data,
      message: response.data?.message,
    };
  } catch (error) {
    console.error('🌐 API CALL: getDrillVocabQuestions - Error:', error);
    console.error('🌐 API CALL: Error response:', error.response);
    return {
      success: false,
      message: error.response?.data?.message || error.message,
    };
  }
};

// Submit Drill Vocab Answers
// POST /frontsite-api/v1/learner/learning/vocab-drill/submit
// Body: { "data": { "session-id": "uuid", "user-answers": [...] } }
export const submitDrillVocab = async ({ sessionId, userAnswers }) => {
  try {
    console.log('🌐 API CALL: submitDrillVocab - Starting request...');

    const missingFields = [];
    if (!sessionId) missingFields.push('session-id');
    if (!userAnswers || !Array.isArray(userAnswers) || userAnswers.length === 0) {
      missingFields.push('user-answers (must be non-empty array)');
    }

    if (missingFields.length > 0) {
      const errorMsg = `Missing required fields: ${missingFields.join(', ')}`;
      console.error('🌐 API CALL: submitDrillVocab - Validation error:', errorMsg);
      return { success: false, message: errorMsg };
    }

    const payload = {
      data: {
        'session-id': sessionId,
        'user-answers': userAnswers,
      },
    };

    console.log('🌐 API CALL: URL:', api.defaults.baseURL + '/frontsite-api/v1/learner/learning/vocab-drill/submit');
    console.log('🌐 API CALL: Body:', payload);

    const response = await api.post('/frontsite-api/v1/learner/learning/vocab-drill/submit', payload, {
      timeout: 120000,
    });

    console.log('🌐 API CALL: submitDrillVocab - Response received:', response);

    if (response.data?.status === 'error') {
      return {
        success: false,
        status: 'error',
        data: response.data?.data,
        message: response.data?.message || 'Failed to submit vocab drill',
      };
    }

    return {
      success: response.data?.status === 'ok',
      status: response.data?.status,
      data: response.data?.data,
      message: response.data?.message,
    };
  } catch (error) {
    console.error('🌐 API CALL: submitDrillVocab - Error:', error);
    console.error('🌐 API CALL: Error response:', error.response);
    return {
      success: false,
      status: 'error',
      message: error.response?.data?.message || error.message,
    };
  }
};

// Get available practice modules
export const getPracticeModules = async () => {
  try {
    const response = await api.get('/practice/modules');
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message || error.message,
    };
  }
};

// Get practice questions by module ID
export const getPracticeQuestions = async (moduleId) => {
  try {
    const response = await api.get(`/practice/modules/${moduleId}/questions`);
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message || error.message,
    };
  }
};

// Submit practice answer
export const submitPracticeAnswer = async (questionId, answer) => {
  try {
    const response = await api.post(`/practice/questions/${questionId}/submit`, { answer });
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message || error.message,
    };
  }
};

// Get practice results
export const getPracticeResults = async (practiceId) => {
  try {
    const response = await api.get(`/practice/${practiceId}/results`);
    return {
      success: true,
      data: response.data,
    };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.message || error.message,
    };
  }
};

// Submit Practice Session
// POST /frontsite-api/v1/learner/learning/practice/submit
// Body: { "data": { "session-id", "practice-type", "subpractice-type"?, "course-id"?, "subcourse-id"?, "skill-id"?, "lesson-id"?, "user-answers": [...] } }
export const submitPractice = async ({
  sessionId,
  practiceType,
  subpracticeType,
  courseId,
  subcourseId,
  skillId,
  lessonId,
  userAnswers
}) => {
  try {
    console.log('🌐 API CALL: submitPractice - Starting request...');
    
    const missingFields = [];
    if (!sessionId) missingFields.push('session-id');
    if (!practiceType) missingFields.push('practice-type');
    if (!userAnswers || !Array.isArray(userAnswers) || userAnswers.length === 0) {
      missingFields.push('user-answers (must be non-empty array)');
    }
    
    // At least one of course-id, subcourse-id, skill-id, or lesson-id is required
    if (!courseId && !subcourseId && !skillId && !lessonId) {
      missingFields.push('course-id, subcourse-id, skill-id, or lesson-id (at least one is required)');
    }
    
    if (missingFields.length > 0) {
      const errorMsg = `Missing required fields: ${missingFields.join(', ')}`;
      console.error('🌐 API CALL: submitPractice - Validation error:', errorMsg);
      return {
        success: false,
        message: errorMsg,
      };
    }
    
    const payload = {
      data: {
        'session-id': sessionId,
        'practice-type': practiceType,
        'user-answers': userAnswers,
      },
    };
    
    if (subpracticeType) payload.data['subpractice-type'] = subpracticeType;
    // Priority: lesson-id > skill-id > subcourse-id > course-id
    if (lessonId) {
      payload.data['lesson-id'] = lessonId;
    } else if (skillId) {
      payload.data['skill-id'] = skillId;
    } else if (subcourseId) {
      payload.data['subcourse-id'] = subcourseId;
    } else if (courseId) {
      payload.data['course-id'] = courseId;
    }
    
    // Save user-answers to localStorage with key "user-answers"
    try {
      const storageData = {
        'session-id': sessionId,
        'practice-type': practiceType,
        'subpractice-type': subpracticeType || null,
        'user-answers': userAnswers,
        timestamp: new Date().toISOString(),
      };
      localStorage.setItem('user-answers', JSON.stringify(storageData));
      console.log('💾 Saved user-answers to localStorage:', storageData);
    } catch (storageError) {
      console.warn('⚠️ Failed to save user-answers to localStorage:', storageError);
    }
    
    console.log('🌐 API CALL: URL:', api.defaults.baseURL + '/frontsite-api/v1/learner/learning/practice/submit');
    console.log('🌐 API CALL: Input params:', { courseId, subcourseId, skillId });
    console.log('🌐 API CALL: Body:', payload);
    console.log('🌐 API CALL: Body.data:', payload.data);
    
    const response = await api.post('/frontsite-api/v1/learner/learning/practice/submit', payload, {
      timeout: 120000, // 2 minutes timeout for practice submission
    });
    
    console.log('🌐 API CALL: submitPractice - Response received:', response);
    console.log('🌐 API CALL: Response data:', response.data);
    console.log('🌐 API CALL: Response data.data:', response.data?.data);
    console.log('🌐 API CALL: Response status:', response.data?.status);
    
    // Check if response status is error (like FE does)
    if (response.data?.status === 'error') {
      const errorMessage = response.data?.message || 'Failed to submit practice session';
      console.error('🌐 API CALL: submitPractice - Error status in response:', errorMessage);
      return {
        success: false,
        status: 'error',
        data: response.data?.data,
        message: errorMessage,
      };
    }
    
    // Only proceed if status is 'ok'
    if (response.data?.status !== 'ok') {
      const errorMessage = response.data?.message || 'Unexpected response status from server';
      console.error('🌐 API CALL: submitPractice - Unexpected status:', response.data?.status);
      return {
        success: false,
        status: response.data?.status || 'unknown',
        data: response.data?.data,
        message: errorMessage,
      };
    }
    
    console.log('🌐 API CALL: Response data.data:', response.data?.data);
    console.log('🌐 API CALL: Looking for request-id in:', {
      'response.data.data': response.data?.data,
      'response.data.data.request-id': response.data?.data?.['request-id'],
      'response.data.request-id': response.data?.['request-id'],
      'response.data.data.requestId': response.data?.data?.requestId,
    });
    
    // Try multiple possible locations for request-id
    const requestId = 
      response.data?.data?.['request-id'] || 
      response.data?.['request-id'] || 
      response.data?.data?.requestId ||
      response.data?.requestId ||
      null;
    
    console.log('🌐 API CALL: Extracted request-id:', requestId);
    
    return {
      success: true,
      status: 'ok',
      data: response.data?.data,
      message: response.data?.message,
      requestId: requestId, // For async submissions
    };
  } catch (error) {
    console.error('🌐 API CALL: submitPractice - Error:', error);
    console.error('🌐 API CALL: Error response:', error.response);
    
    // Handle HTTP errors (400, 500, etc) - axios throws these
    if (error.response) {
      // Backend returned error response
      return {
        success: false,
        status: 'error',
        message: error.response?.data?.message || error.message || 'Failed to submit practice session',
        httpStatus: error.response?.status,
      };
    }
    
    // Network errors, timeouts, etc
    return {
      success: false,
      status: 'error',
      message: error.message || 'Network error occurred while submitting practice',
    };
  }
};

// Submit Speaking Practice
// POST /frontsite-api/v1/learner/learning/practice/submit-speaking
// Content-Type: multipart/form-data
// Parts: session-id, lesson-id, course-id, subcourse-id, proset-id, soal-id, duration (ms), universal-learning-id, audio (blob), lang-code (optional)
export const submitSpeaking = async ({
  sessionId,
  lessonId,
  courseId,
  subcourseId,
  prosetId,
  soalId,
  duration,
  universalLearningId,
  audioBlob,
  langCode,
  audioFilename,
  audioContentType,
  recordingId,
}) => {
  try {
    console.log('🌐 API CALL: submitSpeaking - Starting request...');
    console.log('🌐 API CALL: Parameters:', {
      sessionId,
      lessonId,
      courseId,
      subcourseId,
      prosetId,
      soalId,
      duration,
      universalLearningId,
      hasAudioBlob: !!audioBlob,
      langCode,
      audioFilename,
      audioContentType,
      recordingId,
    });
    
    const missingFields = [];
    if (!sessionId) missingFields.push('session-id');
    if (!prosetId) missingFields.push('proset-id');
    if (!soalId) missingFields.push('soal-id');
    if (!duration && duration !== 0) missingFields.push('duration');
    if (!universalLearningId) missingFields.push('universal-learning-id');
    if (!audioBlob) missingFields.push('audio');
    
    if (missingFields.length > 0) {
      const errorMsg = `Missing required fields: ${missingFields.join(', ')}`;
      console.error('🌐 API CALL: submitSpeaking - Validation error:', errorMsg);
      return {
        success: false,
        message: errorMsg,
      };
    }

    // Dev-only: force Speaking.jsx error state (Retry / Re-record) without hitting the API
    if (process.env.REACT_APP_MOCK_SPEAKING_SUBMIT_ERROR === 'true') {
      console.log('🧪 MOCK: submitSpeaking — REACT_APP_MOCK_SPEAKING_SUBMIT_ERROR (error UI)');
      await new Promise((r) => setTimeout(r, 600));
      return {
        success: false,
        message: SPEAKING_SUBMIT_FAILED_USER_MESSAGE,
      };
    }
    
    const formData = new FormData();
    formData.append('session-id', sessionId);
    // Priority: lesson-id > subcourse-id > course-id
    if (lessonId) {
      formData.append('lesson-id', lessonId);
    } else if (subcourseId) {
      formData.append('subcourse-id', subcourseId);
    } else if (courseId) {
      formData.append('course-id', courseId);
    }
    formData.append('proset-id', prosetId);
    formData.append('soal-id', soalId);
    formData.append('duration', duration.toString());
    formData.append('universal-learning-id', universalLearningId);

    // Resolve upload identity: prefer caller-supplied meta, else derive from blob.type.
    const fallbackMeta = getAudioFileMeta(audioContentType || audioBlob?.type);
    const resolvedFilename = audioFilename || fallbackMeta.filename;
    const resolvedContentType = audioContentType || fallbackMeta.contentType;
    const audioFile = audioBlob instanceof File
      ? audioBlob
      : new File([audioBlob], resolvedFilename, { type: resolvedContentType });
    formData.append('audio', audioFile);

    if (recordingId) {
      formData.append('recording-id', recordingId);
    }

    // changed by Cursor: append lang-code if provided (optional)
    if (langCode) {
      formData.append('lang-code', langCode);
    }
    
    console.log('🌐 API CALL: URL:', api.defaults.baseURL + '/frontsite-api/v1/learner/learning/practice/submit-speaking');
    console.log('🌐 API CALL: FormData keys:', Array.from(formData.keys()));
    
    const response = await api.post(
      '/frontsite-api/v1/learner/learning/practice/submit-speaking',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 120000, // 2 minutes timeout for audio upload
        meta: {
          page: 'SpeakingPractice',
          action: 'submit-speaking',
          practiceSessionId: sessionId || null,
          recordingId: recordingId || null,
        },
      }
    );
    
    console.log('🌐 API CALL: submitSpeaking - Response received:', response);
    console.log('🌐 API CALL: submitSpeaking - Response data:', response.data);
    console.log('🌐 API CALL: submitSpeaking - Response data.data:', response.data?.data);
    
    // Extract request-id if present (for async processing)
    const requestId = 
      response.data?.data?.['request-id'] || 
      response.data?.['request-id'] || 
      response.data?.data?.requestId ||
      response.data?.requestId ||
      null;
    
    console.log('🌐 API CALL: submitSpeaking - Extracted request-id:', requestId);
    
    return {
      success: !!response.data?.data || !!response.data?.status,
      data: response.data?.data,
      message: response.data?.message,
      requestId: requestId, // Include request-id in return for polling
    };
  } catch (error) {
    console.error('🌐 API CALL: submitSpeaking - Error:', error);
    console.error('🌐 API CALL: Error response:', error.response);
    return {
      success: false,
      message: error.response?.data?.message || error.message,
    };
  }
};

// Get Practice Status (for async submissions)
// POST /frontsite-api/v1/learner/learning/practice/status
// Body: { "data": { "request-id": "uuid" } }
export const getPracticeStatus = async (requestId, trackingMeta = {}) => {
  // Mock error responses for testing async grading error handling
  if (process.env.REACT_APP_MOCK_POLLING_ERROR_200 === 'true') {
    console.log('🧪 MOCK: Returning HTTP 200 + status:"error" for polling');
    await new Promise(r => setTimeout(r, 300));
    return {
      success: false,
      status: 'error',
      httpStatus: 200,
      data: null,
      message: 'Submission timeout, silahkan direfresh kembali',
    };
  }
  if (process.env.REACT_APP_MOCK_POLLING_ERROR_500 === 'true') {
    console.log('🧪 MOCK: Returning HTTP 500 + status:"error" for polling');
    await new Promise(r => setTimeout(r, 300));
    return {
      success: false,
      status: 'error',
      httpStatus: 500,
      data: null,
      message: 'Mohon maaf, penilaian gagal dilakukan. Anda dapat mencoba refresh',
    };
  }

  try {
    console.log('🌐 API CALL: getPracticeStatus - Checking status for request:', requestId);
    
    if (!requestId) {
      return {
        success: false,
        status: 'error',
        message: 'request-id is required',
      };
    }
    
    // Validate requestId format (should be a non-empty string)
    if (typeof requestId !== 'string' || requestId.trim() === '') {
      console.error('🌐 API CALL: getPracticeStatus - Invalid requestId format:', requestId);
      return {
        success: false,
        status: 'error',
        message: 'Invalid request-id format',
      };
    }
    
    const payload = {
      data: {
        'request-id': requestId.trim(),
      },
    };
    
    console.log('🌐 API CALL: getPracticeStatus - URL:', api.defaults.baseURL + '/frontsite-api/v1/learner/learning/practice/status');
    console.log('🌐 API CALL: getPracticeStatus - Payload:', payload);
    
    const response = await api.post('/frontsite-api/v1/learner/learning/practice/status', payload, {
      meta: {
        page: trackingMeta.page || 'Practice',
        action: trackingMeta.action || 'poll-status',
        practiceSessionId: trackingMeta.practiceSessionId || null,
      },
    });
    
    console.log('🌐 API CALL: getPracticeStatus - Response:', response);
    console.log('🌐 API CALL: getPracticeStatus - Response data:', response.data);
    console.log('🌐 API CALL: getPracticeStatus - Response status:', response.data?.status);
    
    // Handle different response statuses
    const responseStatus = response.data?.status;
    
    // If API returns error status in response.data.status (HTTP 200 + body error = retryable)
    if (responseStatus === 'error') {
      return {
        success: false,
        status: 'error',
        httpStatus: 200,
        data: response.data?.data,
        message: response.data?.message || '',
      };
    }
    
    return {
      success: true,
      status: responseStatus, // 'processing', 'ok', 'error'
      httpStatus: 200,
      data: response.data?.data,
      message: response.data?.message,
    };
  } catch (error) {
    console.error('🌐 API CALL: getPracticeStatus - Error:', error);
    console.error('🌐 API CALL: getPracticeStatus - Error response:', error.response);
    console.error('🌐 API CALL: getPracticeStatus - Error response data:', error.response?.data);
    
    // Check if error response has status field
    if (error.response?.data?.status) {
      return {
        success: false,
        status: error.response.data.status,
        httpStatus: error.response?.status,
        data: error.response.data?.data,
        message: error.response.data?.message || error.message,
      };
    }
    
    return {
      success: false,
      status: 'error',
      httpStatus: error.response?.status,
      message: error.response?.data?.message || error.message || '',
    };
  }
};

// Get or Generate Pronunciation Audio
// POST /frontsite-api/v1/learner/learning/practice/pronun-audio
// Body: { "data": { "text-to-read": "Ich bin Anna", "lang-code": "de" } }
// Returns: { "status": "ok", "data": { "text": "...", "_id": "...", "stream-url": "..." } }
export const getPronunAudio = async (textToRead, langCode = 'de') => {
  try {
    console.log('🌐 API CALL: getPronunAudio - Starting request...');
    console.log('🌐 API CALL: text-to-read:', textToRead);
    console.log('🌐 API CALL: lang-code:', langCode);

    if (!textToRead || !textToRead.trim()) {
      const errorMsg = 'text-to-read is required';
      console.error('🌐 API CALL: getPronunAudio - Validation error:', errorMsg);
      return {
        success: false,
        message: errorMsg,
      };
    }

    const payload = {
      data: {
        'text-to-read': textToRead.trim(),
        'lang-code': langCode,
      },
    };
    
    console.log('🌐 API CALL: URL:', api.defaults.baseURL + '/frontsite-api/v1/learner/learning/practice/pronun-audio');
    console.log('🌐 API CALL: Body:', payload);
    
    const response = await api.post('/frontsite-api/v1/learner/learning/practice/pronun-audio', payload);
    
    console.log('🌐 API CALL: getPronunAudio - Response received:', response);
    console.log('🌐 API CALL: getPronunAudio - Response data:', response.data);
    
    if (response.data?.status === 'ok') {
      return {
        success: true,
        data: response.data?.data,
        message: response.data?.message,
      };
    } else {
      return {
        success: false,
        message: response.data?.message || 'Failed to get pronunciation audio',
      };
    }
  } catch (error) {
    console.error('🌐 API CALL: getPronunAudio - Error:', error);
    console.error('🌐 API CALL: Error response:', error.response);
    return {
      success: false,
      message: error.response?.data?.message || error.message || 'Failed to get pronunciation audio',
    };
  }
};

// Submit Pronunciation Assessment
// POST /frontsite-api/v1/learner/learning/practice/submit-pronun
// Content-Type: multipart/form-data
// Parts: course-id, subcourse-id, session-id, proset-id, soal-id, text-to-read, duration (ms), universal-learning-id, audio (WAV file)
export const submitPronunciation = async ({
  lessonId,
  courseId,
  subcourseId,
  sessionId,
  prosetId,
  soalId,
  textToRead,
  duration,
  universalLearningId,
  audioBlob,
  langCode
}) => {
  try {
    console.log('🌐 API CALL: submitPronunciation - Starting request...');
    console.log('🌐 API CALL: Parameters:', {
      lessonId,
      courseId,
      subcourseId,
      sessionId,
      prosetId,
      soalId,
      textToRead,
      duration,
      universalLearningId,
      hasAudioBlob: !!audioBlob,
      langCode
    });
    
    // Validate required fields
    // Note: lesson-id OR subcourse-id OR course-id is required (at least one)
    const missingFields = [];
    if (!lessonId && !subcourseId && !courseId) {
      missingFields.push('lesson-id, subcourse-id, or course-id (one is required)');
    }
    if (!sessionId) missingFields.push('session-id');
    if (!prosetId) missingFields.push('proset-id');
    if (!soalId) missingFields.push('soal-id');
    if (!textToRead) missingFields.push('text-to-read');
    if (!duration && duration !== 0) missingFields.push('duration');
    if (!universalLearningId) missingFields.push('universal-learning-id');
    if (!audioBlob) missingFields.push('audio');
    
    if (missingFields.length > 0) {
      const errorMsg = `Missing required fields: ${missingFields.join(', ')}`;
      console.error('🌐 API CALL: submitPronunciation - Validation error:', errorMsg);
      return {
        success: false,
        message: errorMsg,
      };
    }
    
    // Create FormData for multipart/form-data
    const formData = new FormData();
    
    // Append all required fields
    // changed by Cursor: prioritize lesson-id over subcourse-id/course-id
    if (lessonId) {
      formData.append('lesson-id', lessonId);
    } else if (subcourseId) {
      formData.append('subcourse-id', subcourseId);
    } else if (courseId) {
      formData.append('course-id', courseId);
    }
    formData.append('session-id', sessionId);
    formData.append('proset-id', prosetId);
    formData.append('soal-id', soalId);
    formData.append('text-to-read', textToRead);
    formData.append('duration', duration.toString());
    formData.append('universal-learning-id', universalLearningId);

    // Append lang-code if provided (optional)
    if (langCode) {
      formData.append('lang-code', langCode);
    }

    // Append audio blob as WAV file
    // Ensure it's a WAV file - convert if needed
    const audioFile = audioBlob instanceof File 
      ? audioBlob 
      : new File([audioBlob], 'recording.wav', { type: 'audio/wav' });
    formData.append('audio', audioFile);

    console.log('🌐 API CALL: URL:', api.defaults.baseURL + '/frontsite-api/v1/learner/learning/practice/submit-pronun');
    console.log('🌐 API CALL: FormData keys:', Array.from(formData.keys()));
    
    // Log FormData values (except audio blob)
    for (const [key, value] of formData.entries()) {
      if (key !== 'audio') {
        console.log(`🌐 API CALL: FormData[${key}]:`, value);
      } else {
        console.log(`🌐 API CALL: FormData[${key}]:`, value.name, value.type, value.size, 'bytes');
      }
    }

    const response = await api.post('/frontsite-api/v1/learner/learning/practice/submit-pronun', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 120000, // 2 minutes timeout for audio upload
    });
    
    console.log('🌐 API CALL: submitPronunciation - Response received:', response);
    
    return {
      success: response.data?.status === 'success',
      data: response.data?.data,
      message: response.data?.message,
    };
  } catch (error) {
    console.error('🌐 API CALL: submitPronunciation - Error:', error);
    console.error('🌐 API CALL: Error response:', error.response);
    console.error('🌐 API CALL: Error response data:', error.response?.data);
    return {
      success: false,
      message: error.response?.data?.message || error.message,
    };
  }
};

